import { useCallback } from 'react'
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react'
import { isProduction, isSandbox, isTest } from 'utils/environment'

export const FingerprintFlows = {
  LOGIN: 'LOGIN',
  FORGOT_PASSWORD: 'FORGOT_PASSWORD',
  RESET_PASSWORD: 'RESET_PASSWORD',
} as const

type Flow = (typeof FingerprintFlows)[keyof typeof FingerprintFlows]
interface TrackFingerprintProps {
  flow?: Flow
  merchantName: string
  accountId: number
  accountReference: string
  profileId: number
}

const isFingerprintEnabled = isSandbox || isProduction || isTest
const FINGERPRINT_SOURCE = 'BVNK Merchant Portal'

export const useFingerprint = () => {
  const { getData, isLoading, error, data } = useVisitorData(
    { extendedResult: true },
    { immediate: false }
  )

  const trackFingerprint = useCallback(
    ({
      merchantName,
      accountId,
      accountReference,
      profileId,
      flow = FingerprintFlows.LOGIN,
    }: TrackFingerprintProps) => {
      const enableFingerprint = !!flow && !!merchantName && !!accountId && !!profileId

      if (!enableFingerprint || !isFingerprintEnabled) return

      return getData({
        tag: {
          source: FINGERPRINT_SOURCE,
          flow,
          merchantName,
          accountId,
          accountReference,
        },
      })
    },
    [getData]
  )

  const getFingerprintEventId = useCallback(
    async (flow: Flow): Promise<string> => {
      if (!isFingerprintEnabled) return ''

      const result = await getData({
        tag: { source: FINGERPRINT_SOURCE, flow },
      })

      return result?.requestId ?? ''
    },
    [getData]
  )

  return { trackFingerprint, getFingerprintEventId, isLoading, error, data }
}
